import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus } from 'lucide-react';
import AdminCreditsManagement from './AdminCreditsManagement';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Stats {
  totalUsers: number;
  activeUsers: number;
  proUsers: number;
  freeUsers: number;
  adminUsers: number;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  subscriptionType: string;
  isActive: boolean;
  emailVerified: boolean;
  createdAt: string;
}

interface Specialist {
  id: string;
  name: string;
  title?: string;
  specialtyType: string;
  phone: string;
  email?: string;
  address: string;
  city: string;
  county: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  submittedById?: string;
  createdAt: string;
}

interface FoodProduct {
  id: string;
  name: string;
  brand?: string;
  barcode?: string;
  servingSize: number;
  servingUnit: string;
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber?: number;
    sugar?: number;
    sodium?: number;
  };
  imageUrl?: string;
  isCustom: boolean;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
}

export default function AdminDashboard() {
  const { user, accessToken, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [allSpecialists, setAllSpecialists] = useState<Specialist[]>([]);
  const [products, setProducts] = useState<FoodProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'specialists' | 'allSpecialists' | 'cache' | 'products' | 'credits'>('users');
  const [caches, setCaches] = useState<{foodLists: any[], exerciseLists: any[], total: number} | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    name: '',
    title: '',
    specialtyType: 'RHEUMATOLOGIST',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: '',
    county: 'București',
    description: '',
    education: '',
    experience: '',
    acceptsNewPatients: true,
    specializations: ''
  });
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }

    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, specialistsRes, cacheRes, allSpecialistsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        axios.get(`${API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        axios.get(`${API_URL}/api/specialists?status=PENDING`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        // Get cache data
        axios.get(`${API_URL}/api/admin/cache`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }).catch(() => ({ data: { foodLists: [], exerciseLists: [], total: 0 } })),
        // Get all specialists for admin tab
        Promise.all([
          axios.get(`${API_URL}/api/specialists?status=APPROVED`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          axios.get(`${API_URL}/api/specialists?status=PENDING`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          axios.get(`${API_URL}/api/specialists?status=REJECTED`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
        ]).then(([approved, pending, rejected]) => ({
          data: {
            specialists: [
              ...(approved.data.specialists || []),
              ...(pending.data.specialists || []),
              ...(rejected.data.specialists || [])
            ]
          }
        })),
        // Get all food products
        axios.get(`${API_URL}/api/admin/products`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }).catch(() => ({ data: { products: [] } }))
      ]);

      setStats(statsRes.data.stats);
      setUsers(usersRes.data.users);
      setSpecialists(specialistsRes.data.specialists || []);
      setCaches(cacheRes.data);
      setAllSpecialists(allSpecialistsRes.data.specialists || []);
      setProducts(productsRes.data.products || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId: string, currentStatus: boolean) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${userId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la actualizare');
    }
  };

  const handleChangeSubscription = async (userId: string, newType: string) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${userId}`,
        { subscriptionType: newType },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la actualizare');
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!confirm(`Sigur vrei să ștergi utilizatorul ${email}?`)) return;

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergere');
    }
  };

  const handleApproveSpecialist = async (specialistId: string) => {
    try {
      await axios.post(
        `${API_URL}/api/specialists/${specialistId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('Specialist aprobat! ✅');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la aprobare');
    }
  };

  const handleRejectSpecialist = async (specialistId: string, name: string) => {
    if (!confirm(`Sigur vrei să respingi specialistul ${name}?`)) return;
    
    try {
      await axios.post(
        `${API_URL}/api/specialists/${specialistId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('Specialist respins! ❌');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la respingere');
    }
  };

  const handleDeleteSpecialist = async (specialistId: string, name: string) => {
    if (!confirm(`Sigur vrei să ștergi specialistul ${name}?`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/specialists/${specialistId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('Specialist șters! ✅');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergere');
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleAddSpecialist = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let avatarUrl = '';
      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        const uploadRes = await axios.post(`${API_URL}/api/specialists/upload-avatar`, formData, { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' } });
        avatarUrl = uploadRes.data.avatarUrl;
      }
      await axios.post(
        `${API_URL}/api/specialists`,
        { ...addFormData, avatarUrl, specializations: addFormData.specializations.split(',').map(s => s.trim()).filter(Boolean) },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('✅ Specialist adăugat cu succes!');
      setShowAddModal(false);
      setAddFormData({ name: '', title: '', specialtyType: 'RHEUMATOLOGIST', phone: '', email: '', website: '', address: '', city: '', county: 'București', description: '', education: '', experience: '', acceptsNewPatients: true, specializations: '' });
      setAvatarFile(null);
      setAvatarPreview('');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la adăugare');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Aprobat</span>;
      case 'PENDING':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">În Așteptare</span>;
      case 'REJECTED':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">Respins</span>;
      default:
        return null;
    }
  };

  const specialtyLabels: Record<string, string> = {
    // Doctori
    FAMILY_DOCTOR: 'Medic de familie', INTERNAL_MEDICINE: 'Medicină internă',
    RHEUMATOLOGIST: 'Reumatolog', IMMUNOLOGIST: 'Imunolog', ALLERGOLOGIST: 'Alergolog',
    NEUROLOGIST: 'Neurolog', CARDIOLOGIST: 'Cardiolog', PULMONOLOGIST: 'Pneumolog',
    GASTROENTEROLOGIST: 'Gastroenterolog', ENDOCRINOLOGIST: 'Endocrinolog',
    DIABETOLOGIST: 'Diabetolog', NEPHROLOGIST: 'Nefrolog', UROLOGIST: 'Urolog',
    GYNECOLOGIST: 'Ginecolog', DERMATOLOGIST: 'Dermatolog', HEMATOLOGIST: 'Hematolog',
    PSYCHIATRIST: 'Psihiatru', ENT_DOCTOR: 'ORL', OPHTHALMOLOGIST: 'Oftalmolog',
    ONCOLOGIST: 'Oncolog', GENETICIST: 'Medic genetician', PEDIATRICIAN: 'Pediatru',
    GERIATRICIAN: 'Geriatru', ORTHOPEDIST: 'Ortoped', REHABILITATION_DOCTOR: 'Medic recuperare medicală',
    // Specialiști
    PHYSIOTHERAPIST: 'Kinetoterapeut', PHYSICAL_THERAPIST: 'Fizioterapeut',
    OCCUPATIONAL_THERAPIST: 'Ergoterapeut', PSYCHOLOGIST: 'Psiholog',
    PSYCHOTHERAPIST: 'Psihoterapeut', NUTRITIONIST: 'Nutriționist', DIETITIAN: 'Dietetician',
    SPEECH_THERAPIST: 'Logoped', MASSAGE_THERAPIST: 'Maseur medical',
    SOCIAL_WORKER: 'Asistent social', GENETIC_COUNSELOR: 'Consilier genetic',
    HOME_CAREGIVER: 'Îngrijitor la domiciliu',
  };
  const romanianCounties = ['Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brașov', 'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj', 'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea', 'Vrancea'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
              ADMIN
            </div>
            <span className="text-gray-600">FII FIT Panel</span>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Dashboard User
            </button>
            <button
              onClick={logout}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Deconectare
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bine ai venit, Admin! 👋
          </h1>
          <p className="text-gray-600">Gestionează utilizatorii și statisticile aplicației</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Quick Actions - Modules */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/education')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-lg">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Education Module</h3>
                  <p className="text-white/80 text-sm">Categorii, articole și FAQs</p>
                </div>
              </div>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/community')}
            className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-lg">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Moderare Comunitate</h3>
                  <p className="text-white/80 text-sm">Posts, raportări și utilizatori</p>
                </div>
              </div>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/ai-usage')}
            className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-lg">
                  <span className="text-3xl">🤖</span>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">AI Usage & Costs</h3>
                  <p className="text-white/80 text-sm">Costuri FII FIT AI per modul</p>
                </div>
              </div>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/push-notifications')}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-lg">
                  <span className="text-3xl">🔔</span>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Push Notifications</h3>
                  <p className="text-white/80 text-sm">Trimite mesaje custom la utilizatori</p>
                </div>
              </div>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <div className="text-sm text-gray-600 mb-1">Total Utilizatori</div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalUsers}</div>
            </div>
            <div className="bg-blue-50 rounded-xl shadow-md p-6 border border-blue-100">
              <div className="text-sm text-blue-600 mb-1">Activi</div>
              <div className="text-3xl font-bold text-blue-900">{stats.activeUsers}</div>
            </div>
            <div className="bg-purple-50 rounded-xl shadow-md p-6 border border-purple-100">
              <div className="text-sm text-purple-600 mb-1">PRO</div>
              <div className="text-3xl font-bold text-purple-900">{stats.proUsers}</div>
            </div>
            <div className="bg-gray-50 rounded-xl shadow-md p-6 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">FREE</div>
              <div className="text-3xl font-bold text-gray-900">{stats.freeUsers}</div>
            </div>
            <div className="bg-red-50 rounded-xl shadow-md p-6 border border-red-100">
              <div className="text-sm text-red-600 mb-1">Admini</div>
              <div className="text-3xl font-bold text-red-900">{stats.adminUsers}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-8">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'users'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Utilizatori ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('specialists')}
              className={`flex-1 px-6 py-4 text-sm font-medium relative ${
                activeTab === 'specialists'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Specialiști Pending ({specialists.length})
              {specialists.length > 0 && (
                <span className="ml-2 inline-block w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('allSpecialists')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'allSpecialists'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Toți Specialiștii ({allSpecialists.length})
            </button>
            <button
              onClick={() => setActiveTab('cache')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'cache'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🗄️ Cache ({caches?.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'products'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🍎 Produse ({products.length})
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'credits'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              💳 Credite & Abonamente
            </button>
          </div>
        </div>

        {/* Users Table */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Utilizatori ({users.length})</h2>
            </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nume</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abonament</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{u.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        u.role === 'ADMIN' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={u.subscriptionType}
                        onChange={(e) => handleChangeSubscription(u.id, e.target.value)}
                        disabled={u.role === 'ADMIN'}
                        className="text-sm border border-gray-300 rounded px-2 py-1 disabled:opacity-50"
                      >
                        <option value="FREE">FREE</option>
                        <option value="PRO">PRO</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleActive(u.id, u.isActive)}
                        disabled={u.role === 'ADMIN'}
                        className={`text-xs px-2 py-1 rounded disabled:opacity-50 ${
                          u.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {u.isActive ? 'Activ' : 'Inactiv'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {u.role !== 'ADMIN' && (
                        <button
                          onClick={() => handleDeleteUser(u.id, u.email)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Șterge
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Specialists Table */}
        {activeTab === 'specialists' && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Specialiști Pending Aprobare ({specialists.length})
              </h2>
            </div>

            {specialists.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                Nu sunt specialiști în așteptare
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nume</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tip</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locație</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {specialists.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {s.title && `${s.title} `}{s.name}
                          </div>
                          {s.email && (
                            <div className="text-sm text-gray-500">{s.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {s.specialtyType}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {s.phone}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{s.city}</div>
                          <div className="text-sm text-gray-500">{s.county}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(s.createdAt).toLocaleDateString('ro-RO')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApproveSpecialist(s.id)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                            >
                              Aprobă
                            </button>
                            <button
                              onClick={() => handleRejectSpecialist(s.id, s.name)}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Respinge
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Cache Management */}
        {activeTab === 'cache' && caches && (
          <div className="space-y-6">
            {/* Cache Stats */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-xl shadow-md p-6 border border-green-100">
                <div className="text-sm text-green-600 mb-1">Alimente Cached</div>
                <div className="text-3xl font-bold text-green-900">{caches.foodLists.length}</div>
                <div className="text-xs text-green-600 mt-1">Condiții medicale</div>
              </div>
              <div className="bg-purple-50 rounded-xl shadow-md p-6 border border-purple-100">
                <div className="text-sm text-purple-600 mb-1">Exerciții Cached</div>
                <div className="text-3xl font-bold text-purple-900">{caches.exerciseLists.length}</div>
                <div className="text-xs text-purple-600 mt-1">Combinații condiție+mobilitate</div>
              </div>
              <div className="bg-blue-50 rounded-xl shadow-md p-6 border border-blue-100">
                <div className="text-sm text-blue-600 mb-1">Total Cache-uri</div>
                <div className="text-3xl font-bold text-blue-900">{caches.total}</div>
                <div className="text-xs text-blue-600 mt-1">Date partajate între utilizatori</div>
              </div>
            </div>

            {/* Delete All Button */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-red-900 mb-1">⚠️ Zona Periculoasă</h3>
                  <p className="text-sm text-red-700">Șterge toate cache-urile. Vor fi regenerate automat la următoarea cerere.</p>
                </div>
                <button
                  onClick={async () => {
                    if (!confirm('SIGUR vrei să ștergi TOATE cache-urile? Aceasta este o acțiune ireversibilă!')) return;
                    try {
                      await axios.delete(`${API_URL}/api/admin/cache/all`, {
                        headers: { Authorization: `Bearer ${accessToken}` }
                      });
                      alert('✅ Toate cache-urile au fost șterse!');
                      fetchData();
                    } catch (err: any) {
                      alert(err.response?.data?.error || 'Eroare la ștergere');
                    }
                  }}
                  className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
                >
                  Șterge Tot
                </button>
              </div>
            </div>

            {/* Food Lists Cache */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
                <h2 className="text-xl font-bold text-green-900">Cache Alimente ({caches.foodLists.length})</h2>
                <p className="text-sm text-green-700 mt-1">Liste de alimente permise/interzise per condiție medicală</p>
              </div>
              {caches.foodLists.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  Nu există cache-uri de alimente
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condiție</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actualizat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {caches.foodLists.map((food: any) => (
                        <tr key={food.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 capitalize">
                            {food.condition}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(food.createdAt).toLocaleDateString('ro-RO')}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(food.updatedAt).toLocaleDateString('ro-RO')}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={async () => {
                                if (!confirm(`Ștergi cache-ul pentru ${food.condition}?`)) return;
                                try {
                                  await axios.delete(`${API_URL}/api/admin/cache/foods/${food.condition}`, {
                                    headers: { Authorization: `Bearer ${accessToken}` }
                                  });
                                  alert('✅ Cache șters!');
                                  fetchData();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Eroare');
                                }
                              }}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Șterge
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Exercise Lists Cache */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
                <h2 className="text-xl font-bold text-purple-900">Cache Exerciții ({caches.exerciseLists.length})</h2>
                <p className="text-sm text-purple-700 mt-1">Liste de exerciții video per condiție + nivel mobilitate</p>
              </div>
              {caches.exerciseLists.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  Nu există cache-uri de exerciții
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condiție</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobilitate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actualizat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {caches.exerciseLists.map((ex: any) => (
                        <tr key={ex.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 capitalize">
                            {ex.condition}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                            {ex.mobilityLevel}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(ex.createdAt).toLocaleDateString('ro-RO')}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(ex.updatedAt).toLocaleDateString('ro-RO')}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={async () => {
                                if (!confirm(`Ștergi cache-ul pentru ${ex.condition} + ${ex.mobilityLevel}?`)) return;
                                try {
                                  await axios.delete(`${API_URL}/api/admin/cache/exercises/${ex.id}`, {
                                    headers: { Authorization: `Bearer ${accessToken}` }
                                  });
                                  alert('✅ Cache șters!');
                                  fetchData();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Eroare');
                                }
                              }}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Șterge
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Products Table */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Produse Alimentare ({products.length})
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Toate produsele din baza de date (adăugate de utilizatori sau admin)
              </p>
            </div>

            {products.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                Nu există produse în baza de date
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nutriție</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Porție</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tip</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creat</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="w-10 h-10 rounded object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-xl">
                                🍽️
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              {product.brand && (
                                <div className="text-xs text-gray-500">{product.brand}</div>
                              )}
                              {product.barcode && (
                                <div className="text-xs text-gray-400">Barcode: {product.barcode}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-semibold text-primary-600">
                              {product.nutrition.calories} kcal
                            </div>
                            <div className="text-xs text-gray-600">
                              P: {product.nutrition.protein}g | C: {product.nutrition.carbs}g | F: {product.nutrition.fat}g
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {product.servingSize}{product.servingUnit}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            product.isCustom
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {product.isCustom ? 'Custom' : 'Database'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(product.createdAt).toLocaleDateString('ro-RO')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={async () => {
                                const newName = prompt('Nume produs:', product.name);
                                if (!newName || newName === product.name) return;
                                try {
                                  await axios.patch(
                                    `${API_URL}/api/admin/products/${product.id}`,
                                    { name: newName },
                                    { headers: { Authorization: `Bearer ${accessToken}` } }
                                  );
                                  alert('✅ Produs actualizat!');
                                  fetchData();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Eroare');
                                }
                              }}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            >
                              Editează
                            </button>
                            <button
                              onClick={async () => {
                                if (!confirm(`Sigur vrei să ștergi produsul "${product.name}"?`)) return;
                                try {
                                  await axios.delete(
                                    `${API_URL}/api/admin/products/${product.id}`,
                                    { headers: { Authorization: `Bearer ${accessToken}` } }
                                  );
                                  alert('✅ Produs șters!');
                                  fetchData();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Eroare');
                                }
                              }}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Șterge
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Credits & Subscriptions Management */}
        {activeTab === 'credits' && accessToken && (
          <AdminCreditsManagement accessToken={accessToken} />
        )}

        {/* All Specialists Table */}
        {activeTab === 'allSpecialists' && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Toți Specialiștii ({allSpecialists.length})
              </h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="w-5 h-5" />
                Adaugă Specialist Manual
              </button>
            </div>

            {allSpecialists.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                Nu există specialiști
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nume</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tip</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locație</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allSpecialists.map((s: any) => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {s.title && `${s.title} `}{s.name}
                          </div>
                          {s.email && (
                            <div className="text-sm text-gray-500">{s.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {s.specialtyType}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{s.city}</div>
                          <div className="text-sm text-gray-500">{s.county}</div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(s.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          ⭐ {s.averageRating?.toFixed(1) || '0.0'} ({s.totalReviews || 0})
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/specialists/${s.id}`)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            >
                              Vezi
                            </button>
                            {s.status === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => handleApproveSpecialist(s.id)}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                >
                                  Aprobă
                                </button>
                                <button
                                  onClick={() => handleRejectSpecialist(s.id, s.name)}
                                  className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                                >
                                  Respinge
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDeleteSpecialist(s.id, s.name)}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Șterge
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Specialist Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">Adaugă Specialist Manual</h2>
            </div>
            <form onSubmit={handleAddSpecialist} className="p-6 space-y-4">
              <div className="flex gap-4 mb-4">
                {avatarPreview ? <img src={avatarPreview} className="w-20 h-20 rounded-full object-cover border-2" /> : <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl">📷</div>}
                <label className="flex-1 cursor-pointer"><div className="px-4 py-2 border-2 border-dashed rounded-lg hover:bg-gray-50 text-center"><span className="text-sm">Încarcă Avatar (max 5MB)</span></div><input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" /></label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="Nume *" value={addFormData.name} onChange={(e) => setAddFormData({...addFormData, name: e.target.value})} className="px-3 py-2 border rounded-lg" />
                <input placeholder="Titlu (Dr., Prof.)" value={addFormData.title} onChange={(e) => setAddFormData({...addFormData, title: e.target.value})} className="px-3 py-2 border rounded-lg" />
                <select required value={addFormData.specialtyType} onChange={(e) => setAddFormData({...addFormData, specialtyType: e.target.value})} className="px-3 py-2 border rounded-lg">
                  {Object.entries(specialtyLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input required type="tel" placeholder="Telefon *" value={addFormData.phone} onChange={(e) => setAddFormData({...addFormData, phone: e.target.value})} className="px-3 py-2 border rounded-lg" />
                <input type="email" placeholder="Email" value={addFormData.email} onChange={(e) => setAddFormData({...addFormData, email: e.target.value})} className="px-3 py-2 border rounded-lg" />
                <input type="url" placeholder="Website" value={addFormData.website} onChange={(e) => setAddFormData({...addFormData, website: e.target.value})} className="px-3 py-2 border rounded-lg" />
              </div>
              <input required placeholder="Adresă completă *" value={addFormData.address} onChange={(e) => setAddFormData({...addFormData, address: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="Oraș *" value={addFormData.city} onChange={(e) => setAddFormData({...addFormData, city: e.target.value})} className="px-3 py-2 border rounded-lg" />
                <select required value={addFormData.county} onChange={(e) => setAddFormData({...addFormData, county: e.target.value})} className="px-3 py-2 border rounded-lg">
                  {romanianCounties.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <textarea placeholder="Descriere" value={addFormData.description} onChange={(e) => setAddFormData({...addFormData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} />
              <textarea placeholder="Educație (studii medicale...)" value={addFormData.education} onChange={(e) => setAddFormData({...addFormData, education: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} />
              <textarea placeholder="Experiență (ani practică...)" value={addFormData.experience} onChange={(e) => setAddFormData({...addFormData, experience: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} />
              <input placeholder="Specializări (separate prin virgulă)" value={addFormData.specializations} onChange={(e) => setAddFormData({...addFormData, specializations: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              <label className="flex items-center gap-2"><input type="checkbox" checked={addFormData.acceptsNewPatients} onChange={(e) => setAddFormData({...addFormData, acceptsNewPatients: e.target.checked})} /><span className="text-sm">Acceptă clienți noi</span></label>
              <div className="flex gap-3 pt-4 border-t">
                <button type="submit" disabled={saving} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50">{saving ? 'Se salvează...' : 'Salvează'}</button>
                <button type="button" onClick={() => setShowAddModal(false)} className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Anulează</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
